import { useTeamSchedule } from "./TeamScheduleContext.js";
import RowGrid from "./RowGrid.js";
import TileGrid from "./TileGrid.js";
import { useEffect, useRef, useState } from "react";
import type {
  GroupByStrategiesKeys,
  GroupTeamSchedule
} from "./groupTeamSchedule.js";
import { cn, Spinner, useIsGummy } from "@swivu/shared-ui";
import { DateTime } from "luxon";

type TeamSchedule = ReturnType<typeof useTeamSchedule>["gridData"];

const GUMMY_COLUMN_RAIL = cn(
  "relative",
  "after:content-[''] after:absolute after:right-0 after:top-3 after:bottom-3 after:w-px",
  "after:bg-gradient-to-b after:from-transparent after:via-mono-300 after:to-transparent",
  "dark:after:via-mono-700"
);

const TeamScheduleGrid = <T extends GroupByStrategiesKeys>({
  timeZone,
  customData
}: {
  timeZone: string;
  customData?: GroupTeamSchedule<T>;
}) => {
  const { gridData, isLoading, view, dateRange } = useTeamSchedule();
  const isGummy = useIsGummy();
  const [isFirstFetch, setIsFirstFetch] = useState(true);
  const [teamSchedule, setTeamSchedule] = useState<undefined | TeamSchedule>(
    customData ?? gridData
  );
  const [cachedDateRange, setCachedDateRange] =
    useState<ReturnType<typeof useTeamSchedule>["dateRange"]>(dateRange);

  useEffect(() => {
    if (isFirstFetch && !isLoading) {
      setIsFirstFetch(false);
    }
  }, [isLoading, isFirstFetch]);

  useEffect(() => {
    if (!customData && gridData && gridData.length >= 0 && !isLoading) {
      setTeamSchedule(gridData);
      setCachedDateRange(dateRange);
    }
    if (customData && customData.length >= 0 && !isLoading) {
      setTeamSchedule(customData);
      setCachedDateRange(dateRange);
    }
  }, [gridData, dateRange, customData]);

  const startDate = useRef<string>(
    DateTime.fromISO(new Date(dateRange[0]).toISOString(), {
      zone: timeZone
    }).toFormat("MM/dd/yyyy")
  );
  const endDate = useRef<string>(
    DateTime.fromISO(new Date(dateRange[dateRange.length - 1]).toISOString(), {
      zone: timeZone
    }).toFormat("MM/dd/yyyy")
  );

  useEffect(() => {
    if (!isLoading) {
      startDate.current = DateTime.fromISO(
        new Date(dateRange[0]).toISOString(),
        {
          zone: timeZone
        }
      ).toFormat("MM/dd/yyyy");
      endDate.current = DateTime.fromISO(
        new Date(dateRange[dateRange.length - 1]).toISOString(),
        {
          zone: timeZone
        }
      ).toFormat("MM/dd/yyyy");
    }
  }, [dateRange, timeZone, isLoading]);
  if (isFirstFetch && isLoading && teamSchedule?.length === 0) {
    return (
      <div className={`my-2 flex-1 relative animate-pulse`}>
        <div className="grid grid-cols-5 @3xl:grid-cols-6 h-full  gap-0 px-2 absolute inset-0 ">
          <div
            className={cn(
              "hidden @3xl:block",
              isGummy ? GUMMY_COLUMN_RAIL : "border-r dark:border-mono-700/50"
            )}
          />
          <div
            className={cn(
              isGummy ? GUMMY_COLUMN_RAIL : "border-r dark:border-mono-700/50"
            )}
          />
          <div
            className={cn(
              isGummy ? GUMMY_COLUMN_RAIL : "border-r dark:border-mono-700/50"
            )}
          />
          <div
            className={cn(
              isGummy ? GUMMY_COLUMN_RAIL : "border-r dark:border-mono-700/50"
            )}
          />
          <div
            className={cn(
              isGummy ? GUMMY_COLUMN_RAIL : "border-r dark:border-mono-700/50"
            )}
          />
        </div>
        <div className="grid grid-cols-5 @3xl:grid-cols-6 gap-1 px-2 relative dark:[&>*:nth-child(even)]:bg-transparent [&>*:nth-child(even)]:bg-transparent *:mb-2">
          <div
            className={cn(
              "h-8 bg-gray-200 dark:bg-mono-700/50 w-full col-span-4",
              "rounded-surface-xs"
            )}
          ></div>
          <div
            className={cn(
              "h-8 bg-gray-200 dark:bg-mono-700/50 w-full col-span-2",
              "rounded-surface-xs"
            )}
          ></div>
          <div
            className={cn(
              "h-8 bg-gray-200 dark:bg-mono-700/50 w-full col-span-3",
              "rounded-surface-xs"
            )}
          ></div>
        </div>
      </div>
    );
  }
  // return <div>hi</div>;
  return (
    <div
      className={`relative pt-4 z-0 flex-1  ${
        isLoading ? ` animate-pulse ` : ""
      } `}
    >
      <div className="grid grid-cols-5 @3xl:grid-cols-5  gap-0 px-2 absolute inset-0 ">
        <div
          className={cn(
            "hidden @3xl:hidden",
            isGummy ? GUMMY_COLUMN_RAIL : "border-r dark:border-mono-700/50"
          )}
        />
        <div
          className={cn(
            isGummy ? GUMMY_COLUMN_RAIL : "border-r dark:border-mono-700/50"
          )}
        />
        <div
          className={cn(
            isGummy ? GUMMY_COLUMN_RAIL : "border-r dark:border-mono-700/50"
          )}
        />
        <div
          className={cn(
            isGummy ? GUMMY_COLUMN_RAIL : "border-r dark:border-mono-700/50"
          )}
        />
        <div
          className={cn(
            isGummy ? GUMMY_COLUMN_RAIL : "border-r dark:border-mono-700/50"
          )}
        />
      </div>
      <div className="grid grid-cols-5 @3xl:grid-cols-5 gap-1 px-2 relative dark:[&>*:nth-child(even)]:bg-transparent [&>*:nth-child(even)]:bg-transparent *:mb-2">
        {teamSchedule?.length === 0 && (
          <div
            className={`col-span-5 @3xl:col-span-6 py-12  flex items-center justify-center ${
              isLoading && `  `
            }`}
          >
            {!isFirstFetch && (
              <div
                className={cn(
                  "px-8 relative flex items-center justify-center py-4",
                  isGummy
                    ? cn(
                        "shadow-card",
                        "rounded-surface-md bg-white dark:bg-mono-900 font-extrabold text-mono-700 dark:text-mono-300"
                      )
                    : "border dark:border-mono-700/50 rounded-lg bg-white border-mono-200 shadow-xs dark:bg-mono-900"
                )}
              >
                <div className="flex items-center gap-2">
                  <p className={`opacity-0 w-6`}>
                    <Spinner />
                  </p>
                  <p className="flex-1">
                    No events for {startDate.current} - {endDate.current}
                  </p>
                  <p
                    className={`${
                      isLoading ? "opacity-100 " : "opacity-0 "
                    } transition-opacity w-6`}
                  >
                    <Spinner />
                  </p>
                </div>
              </div>
            )}
          </div>
        )}
        {/* <pre>{JSON.stringify(teamSchedule, null, 2)}</pre> */}
        {view.value === "row" ? (
          <RowGrid
            teamSchedule={teamSchedule}
            dateRange={cachedDateRange}
            timeZone={timeZone}
          />
        ) : (
          <TileGrid
            teamSchedule={teamSchedule}
            dateRange={cachedDateRange}
            timeZone={timeZone}
          />
        )}
      </div>
    </div>
  );
};

export default TeamScheduleGrid;
